import { getServerUrl } from '@hooks/useApi'
import Logger from '@logger'
import isIPad from 'is-ios'
import { get, has, isEmpty, isFunction, result, set } from 'lodash'
import type { CSSProperties } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { reloadWindow } from '../../app_globals'
import Loader from '../Loader'
import type { Function, Yacht3DScene } from './ThreeDScene'
import { setInitialSceneProperties } from './ThreeDScene'
import type { VesselMechanicalData } from './VesselMechanicalData'

type ThreeDViewProps = {
  modelName: string
  exploded?: boolean
  autoRotate?: boolean
  calloutsEnabled?: boolean
  calloutsVisible?: boolean
  vesselData: VesselMechanicalData
  getSceneReference?: (scene: Yacht3DScene | null) => void
}

const loaderStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const renderTargetStyle: CSSProperties = {
  backgroundColor: '#333',
  margin: '0 auto',
  width: '100%',
  height: '100%'
}

export default function ThreeDView (props: ThreeDViewProps) {
  const { modelName } = props
  const logger = Logger.create(`3D view [${modelName}]`)

  const yachtSceneRef = useRef<Yacht3DScene | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDestroyed, setIsDestroyed] = useState(false)
  const [baseUrl] = useState(document.location.pathname)

  const renderTarget = useRef<HTMLDivElement | null>(null)
  const calloutsTarget = useRef<HTMLDivElement | null>(null)

  const updateMechanicalData = () => {
    const yachtScene = yachtSceneRef.current

    if (isDestroyed) {
      return
    } else if (window.location.pathname !== baseUrl) {
      return
    } else if (!yachtScene) {
      return
    } else if (!isFunction(yachtScene.setMechanicalData)) {
      return
    } else if (isEmpty(props.vesselData)) {
      return
    }

    try {
      const { rudderPort: port, rudderStbd: stbd } = props.vesselData.rudder

      const {
        stabilizerForwardPort: forwardPort,
        stabilizerForwardStbd: forwardStbd,
        stabilizerAftPort: aftPort,
        stabilizerAftStbd: aftStbd
      } = props.vesselData.stabilizers

      // also include 'stbd' and 'port' keys as legacy
      const rudder = { ...props.vesselData.rudder, port, stbd }

      // also include 'forwardPort', 'forwardStbd', 'aftPort', 'aftStbd' legacy keys
      const stabilizers = {
        ...props.vesselData.stabilizers,
        forwardPort,
        forwardStbd,
        aftPort,
        aftStbd
      }

      yachtScene.setMechanicalData({ ...props.vesselData, rudder, stabilizers })
    } catch (e) {
      console.error(e)
      if (!isIPad) {
        reloadWindow(`got error in 3D view [${e}]`)
      }
    }
  }

  const getSceneRef = (): Yacht3DScene | null => {
    const scene = get(window, `scenes.${modelName}`, null)

    if (props.getSceneReference) {
      props.getSceneReference(scene)
    }

    return scene
  }

  const isRefactoredVessel = (scene: Yacht3DScene) => has(scene, 'initCallouts')

  const initCallouts = useCallback(
    (scene: Yacht3DScene) => {
      if (!scene) {
        return
      }

      if (!isRefactoredVessel(scene) || !calloutsTarget.current) {
        return
      }

      scene.initCallouts(calloutsTarget.current)
    },
    [calloutsTarget.current]
  )

  useEffect(() => {
    const yachtScene = yachtSceneRef.current

    if (!yachtScene) {
      return
    }

    if (!isRefactoredVessel(yachtScene)) {
      const calloutsWrapper = document.querySelector('#callouts') as HTMLDivElement

      if (!calloutsWrapper) {
        return
      }

      calloutsWrapper.style.display = props.calloutsVisible ? 'block' : 'none'
    }
  }, [props.calloutsVisible, yachtSceneRef.current])

  useEffect(() => {
    if (yachtSceneRef.current?.controls) {
      set(yachtSceneRef.current, 'controls.minDistance', 100)
      set(yachtSceneRef.current, 'controls.maxDistance', 200)
    }
  }, [yachtSceneRef.current])

  const onSceneLoaded = async (scene: Yacht3DScene) => {
    logger.verbose(`loaded scene for model "${modelName}"`)

    if (!isFunction(scene.setMechanicalData)) {
      alert('This model is not compatible with this YIS version')
      return
    }

    yachtSceneRef.current = scene
    setIsLoading(false)
    scene.animate()
    initCallouts(scene)
    setInitialSceneProperties(scene, props.autoRotate)
    updateMechanicalData()
  }

  const insert3dModelScript = () => {
    const threeJsScript: HTMLScriptElement = document.createElement('script')

    threeJsScript.src = getServerUrl(`/storage/3d/${modelName}/main.js`, {
      ts: Date.now()
    })

    threeJsScript.type = 'module'

    threeJsScript.onload = () => {
      logger.verbose('loaded ThreeJS script')

      const scene = getSceneRef()

      if (scene) {
        scene.onSceneLoaded = onSceneLoaded as Function
      } else {
        logger.warn('scene was loaded, but does not contain expected model definition')
      }
    }

    document.body.appendChild(threeJsScript)
  }

  useEffect(() => {
    const yachtScene = yachtSceneRef.current

    if (!yachtScene) {
      logger.warn('cannot toggle exploded mode: yachtScene not present in state')
    } else if (!isFunction(yachtScene.switchExploded)) {
      logger.warn(`cannot perform .switchExploded on model "${modelName}" - not implemented`)
    } else {
      const explodedMode = result(yachtScene, 'switchExploded')
      logger.verbose(`toggleExplodedMode for model "${modelName}": changed to ${explodedMode}`)
    }
  }, [props.exploded])

  useEffect(() => {
    const yachtScene = yachtSceneRef.current

    if (yachtScene) {
      logger.verbose(`toggled autoRotate to ${props.autoRotate}`)
      set(yachtScene, 'controls.autoRotate', props.autoRotate)
    }
  }, [props.autoRotate])

  useEffect(() => {
    if (!has(window, 'scenes')) {
      set(window, 'scenes', {})
    }

    const scene = getSceneRef()
    let hasRenderer = true

    if (!renderTarget.current) {
      return
    } else if (!scene) {
      insert3dModelScript()
    } else if (!scene.renderer || !scene.renderer.domElement) {
      hasRenderer = false
    } else {
      renderTarget.current.appendChild(scene.renderer.domElement)
      setTimeout(() => onSceneLoaded(scene), 100)
    }

    const refreshId = hasRenderer ? setInterval(updateMechanicalData, 5000) : null

    return () => {
      setIsDestroyed(true)
      if (refreshId) {
        clearInterval(refreshId)
      }
    }
  }, [])

  return <>
    {isLoading && <div style={loaderStyle}>
      <Loader />
    </div>}

    <div id="renderTarget" style={renderTargetStyle} ref={renderTarget} />

    <div
      id="callouts-container"
      style={{
        display: props.calloutsVisible ? 'block' : 'none',
        pointerEvents: 'none'
      }}
      ref={calloutsTarget}
    />
  </>
}
